import React, { useContext, useEffect, useState } from 'react';
import Layout from '../../Component/layout';
import ChatContainer from './ChatContainer';
import ChatInputBar from './ChatInputBar/ChatinputBar';
import LeadHeader from './LeadHeaders';
import LeadsList from './LeadList';
import Empty from '../../Media/DashboardV1/empty.gif';
import { useDispatch, useSelector } from 'react-redux';
import './styles.scss';
import {
  emptyMetaData,
  fetchLeadApartmentUnits,
  fetchMetaData
} from '../../Redux/Actions/InboxV1';
import { useLocation, useParams } from 'react-router-dom';
import { Alert, Snackbar } from '@mui/material';
import Toaster from '../../Component/Common/Toaster';
import { MONTHS_LIST } from '../../utils';
import { setGlobalError } from '../../Redux/Reducer/GlobalError';
import {
  socketEmitter,
  socketListener,
  socketRemover
} from '../../utils/socket';
import LeadContainerInfo from '../../Component/LeadContainerInfo';
import { getLead, patchLead } from '../../Redux/util/Apis/leads';
import { getCommunityById } from '../../Redux/util/Apis/communities';
import { GlobalContext } from '../../utils/ContextProviders/GlobalContextProvider';
import ArchiveConfirmation from './ArchiveConfirmation';
import InboxContextProvider from '../../utils/ContextProviders/InboxContextProvider';
import SOPAndAgentNotes from './SOPAndAgentNotes';
import NotificationPopup from './SOPAndAgentNotes/NotificationPopup';
import { InboxChatNdHeader, Wrapper } from './styles.inboxv2';
import MaximisedPnAWidget from '../../Component/MaximizedPnAWidget';
import _ from 'lodash';

const InboxV2 = props => {
  const { history } = props;
  const dispatch = useDispatch();

  const inboxSlice = useSelector(state => state.inboxMessages);

  const {
    inboxMetaData,
    isLeadsEmpty,
    toastMessage,
    updatedLeadId,
    messageSent,
    loadMoreMessages,
    newNoteInsertionInProgress,
    isStageUpdated,
    leadApartmentUnits
  } = inboxSlice;

  const [messagesLoading, setMessagesLoading] = useState(true);
  const [isTabOpen, setIsTabOpen] = useState(false);
  const [socketError, setSocketError] = useState(null);

  // Mirrors the old `socket.onAny(...)` filter for 'error' events. Uses our
  // listener helper so it survives a socket reinit (e.g., LD flag flip during
  // a session). Direct `socket.onAny` no longer works since `socket` is null
  // before the LD-driven bootstrap creates it.
  useEffect(() => {
    const handleError = args => setSocketError(args);
    socketListener('error', handleError);
    return () => socketRemover('error', handleError);
  }, []);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [apartmentId, setApartmentId] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const [chatArchivedMsg, setChatArchivedMsg] = useState('');
  const [snackBarMessage, setSnackbarMessage] = useState({});
  const [archiveClick, setArchiveClick] = useState(false);
  const [open, setOpen] = useState(false);
  const [regularLeads, setRegularLeads] = useState([]);
  const [openSopDrawer, setOpenSopDrawer] = useState(false);
  const [showNewMsgNotification, setShowNewMsgNotification] = useState(false);
  const [isMaximizedPnAOpen, setIsMaximizedPnAOpen] = useState(false);
  const location = useLocation();
  const params = useParams();
  const isOnInboxPage =
    location.pathname === '/inbox' || location.pathname === '/';

  const { userData, updateLeadData, darkMode } = useContext(GlobalContext);
  const userId = userData?.user_id;
  const { leadEnabled, communityEnabled, clientEnabled } =
    inboxMetaData?.ai || {};

  const handlePNAExpansionToggle = e => {
    setIsMaximizedPnAOpen(prevState => {
      return !prevState;
    });
  };

  useEffect(() => {
    if (socketError) {
      dispatch(setGlobalError(socketError));
    }
  }, [socketError]);

  useEffect(() => {
    return () => {
      dispatch(emptyMetaData());
    };
  }, []);

  useEffect(() => {
    if (updatedLeadId) {
      handleLeadChange(updatedLeadId);
    }
  }, [updatedLeadId]);

  const [leadData, setLeadData] = useState({});
  const [leadDataLoading, setLeadDataLoading] = useState(false);

  const applyLeadData = lead => {
    setLeadData(lead || {});
    socketEmitter('getLeadMessages', {
      leadId: lead?._id,
      user: userData
    });
    updateLeadData(lead);
    if (lead?.apartment?._id) {
      setApartmentId(lead.apartment._id);
      dispatch(fetchLeadApartmentUnits({ apartmentId: lead.apartment._id }));
    } else {
      setApartmentId('');
    }
    setSnackbarMessage({});
  };

  const fetchLeadData = async ({ variables } = {}) => {
    const leadId = variables?.leadId;
    if (!leadId) return;
    setLeadDataLoading(true);
    const result = await getLead(leadId);
    setLeadDataLoading(false);
    if (result?.error) {
      dispatch(setGlobalError(result.message));
      return;
    }
    applyLeadData(result);
  };

  const refetchLeadInfo = () =>
    fetchLeadData({ variables: { leadId: selectedLeadId } });

  const getScheduledLeadMessages = leadId => {
    socketEmitter('getLeadScheduledMessages', {
      leadId: leadId,
      user: userData
    });
  };
  const [apartmentData, setApartmentData] = useState(null);
  useEffect(() => {
    if (!apartmentId) {
      setApartmentData(null);
      return;
    }
    let cancelled = false;
    getCommunityById(apartmentId).then(data => {
      if (cancelled || !data || data.error) return;
      // Map the REST snake_case response back into the camelCase shape that
      // downstream consumers (LeadHeader, inboxUtils) already expect, so the
      // migration is contained to this fetch boundary.
      setApartmentData({
        _id: data.id,
        name: data.name,
        timeZone: data.time_zone,
        knockKelseyUserName: data.knock_kelsey_username,
        knockHouseAgentUserName: data.knock_house_agent_username
      });
    });
    return () => {
      cancelled = true;
    };
  }, [apartmentId]);

  const propertyData = leadApartmentUnits || [];

  // Wrapper preserves the GraphQL-style call signature
  // (`leadUpdateMutate({ variables: { leadId, record } })`) used by ~10
  // callsites across the codebase, so they don't need to change. Internally
  // routes to the REST PATCH /leads/:leadId endpoint.
  const leadUpdateMutate = async ({ variables } = {}) => {
    const { leadId, record } = variables ?? {};
    const result = await patchLead(leadId, record);
    if (result?.error) {
      if (record?.isArchived) {
        setSnackbarMessage({
          message: 'Failed to archive the lead. Please try again!',
          type: 'error'
        });
        setArchiveClick(false);
      } else if (record?.isArchived === false) {
        setSnackbarMessage({
          message: 'Failed to un-archive the lead. Please try again!',
          type: 'error'
        });
        setArchiveClick(false);
      } else {
        setSnackbarMessage({
          message: 'Failed to update the lead details!',
          type: 'error'
        });
      }
      return;
    }
    const updatedLead = result?.record;
    if (record?.isArchived) {
      setChatArchivedMsg('Chat is archived');
      fetchNewGuestCards();
      if (isOnInboxPage) {
        const currentLeadIndex = regularLeads?.findIndex(
          ({ _id }) => _id === updatedLead?._id
        );
        handleLeadChange(regularLeads[currentLeadIndex]?._id?._id);
      }
    } else if (record?.disablePrivacy === 'Accessed.') {
      setSnackbarMessage({
        message: 'Sensitive Info Accessed',
        type: 'success'
      });
    } else if (record?.isArchived === false) {
      setChatArchivedMsg('Chat is unarchived');
      fetchNewGuestCards();
    } else {
      setSnackbarMessage({
        message: 'Lead Details updated Successfully',
        type: 'success'
      });
    }
    if (updatedLead) applyLeadData(updatedLead);
  };

  useEffect(() => {
    if (isOnInboxPage && regularLeads?.length > 0 && !selectedLeadId) {
      handleLeadChange(regularLeads?.[0]?._id?._id);
    } else if (!isOnInboxPage && params?.id) {
      handleLeadChange(params.id);
    }
  }, [regularLeads, params]);

  useEffect(() => {
    if (selectedLeadId) {
      fetchLeadData({ variables: { leadId: selectedLeadId } });
      dispatch(fetchMetaData({ leadId: selectedLeadId }));
    }
  }, [selectedLeadId]);

  useEffect(() => {
    if (!isStageUpdated && selectedLeadId) {
      fetchLeadData({ variables: { leadId: selectedLeadId } });
    }
  }, [isStageUpdated]); /// To be removed temporary solution

  useEffect(() => {
    if (chatArchivedMsg) {
      let showMsg = setTimeout(() => {
        setChatArchivedMsg('');
        setArchiveClick(false);
      }, 3000);
      return () => {
        clearTimeout(showMsg);
      };
    }
  }, [chatArchivedMsg, archiveClick]);

  const handleAutofillData = data => {
    const d1 = new Date(data?.availableFrom);
    const date = `${MONTHS_LIST[d1.getMonth()]} ${d1.getDate()} ${d1
      .getFullYear()
      .toString()}`;
    setMessageInput(
      prevState =>
        prevState +
        `An apartment that matches your preferences will be available on ${date} for ${
          data?.rent?.min ? '$' + data?.rent?.min : ''
        }
       per month. Would you like me to schedule a tour for you to see it once it’s available?  `
    );
  };

  useEffect(() => {
    if (toastMessage.message || snackBarMessage.message) {
      setOpen(true);
    } else {
      setOpen(false);
    }
    setTimeout(() => {
      setOpen(false);
    }, 6000);
  }, [toastMessage, snackBarMessage]);

  const SOPDrawerToggleHandler = e => {
    e.preventDefault();
    setOpenSopDrawer(prevState => !prevState);
  };

  const leadContainerProps = {
    apartmentId,
    setMessageInput,
    selectedLeadId,
    leadData,
    handleAutofillData,
    propertyData,
    isOnInboxPage,
    leadUpdateMutate,
    userId,
    setIsTabOpen,
    openSopDrawer,
    SOPDrawerToggleHandler,
    leadDataLoading,
    isMaximizedPnAOpen,
    handlePNAExpansionToggle,
    refetchLeadInfo
  };

  const hideShowNewMessageNotification = () => {
    setShowNewMsgNotification(false);
  };

  const handleLeadChange = leadId => {
    if (selectedLeadId !== leadId) {
      socketRemover(`messages-${selectedLeadId}`);
      socketRemover(`scheduled-messages-${selectedLeadId}`);
      setSelectedLeadId(leadId);
    }
  };

  const handleOutgoingMessage = messageDetails => {
    socketEmitter('outboundMessage', {
      messageDetails,
      user: userData
    });
  };
  const handleOutgoingScheduledMessage = messageDetails => {
    socketEmitter('outboundScheduledMessage', {
      messageDetails,
      user: userData
    });
  };

  const handleNoteCreation = noteDetails => {
    socketEmitter('create-note', {
      noteDetails,
      user: userData
    });
  };

  const handleCallLogCreation = callLogDetails => {
    socketEmitter('create-call-log', {
      callLogDetails,
      user: userData
    });
  };

  const fetchNewGuestCards = () => {
    if (userId && isOnInboxPage) {
      socketEmitter('get-agents-leads', {
        agentId: userId,
        filters: { limit: 6 }
      });
    }
  };

  const updateLeadsState = leads => {
    const newLeads = _.difference(leads, regularLeads);
    if (leads?.length) {
      if (newLeads?.length) {
        setRegularLeads([...leads]);
        setMessagesLoading(false);
      }
    } else {
      setRegularLeads([]);
      setMessagesLoading(false);
    }
  };

  useEffect(() => {
    if (!userId) return undefined;

    // Hoist the handler so it has a stable reference for socketRemover to
    // match on. Anonymous inline handlers can't be deregistered cleanly,
    // which was causing listener accumulation on re-renders / userId changes
    // (Sentry Seer flag).
    const handleAgentsLeads = data => {
      if (data?.success) {
        updateLeadsState([...data.data]);
      }
    };

    socketListener(`send-agents-leads-${userId}`, handleAgentsLeads);
    return () =>
      socketRemover(`send-agents-leads-${userId}`, handleAgentsLeads);
  }, [userId]);

  useEffect(() => {
    if (regularLeads?.length === 0) fetchNewGuestCards();

    if (regularLeads?.length < 7 && regularLeads?.length >= 0) {
      const interval = setInterval(() => fetchNewGuestCards(), 30000);

      return () => clearInterval(interval);
    }
  }, [regularLeads?.length]);

  return (
    <Layout disableActions={isTabOpen || openSopDrawer} history={history}>
      <Toaster />
      {!messagesLoading && isOnInboxPage && regularLeads.length === 0 ? (
        <div className="flex-col">
          <div className="inboxChatConatiner" style={{ margin: 'auto' }}>
            <img src={Empty} alt="Empty Gif" />
          </div>
          <div
            style={{
              textAlign: 'center',
              color: darkMode ? '#fff' : ''
            }}
            className="inter-500px-16px"
          >
            No leads are available right now
          </div>
        </div>
      ) : (
        <>
          <Snackbar
            open={open}
            autoHideDuration={3000}
            onClose={() => {
              setOpen(false);
            }}
            action={'Success'}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert severity={toastMessage.severity || snackBarMessage?.type}>
              {toastMessage?.message || snackBarMessage?.message}
            </Alert>
          </Snackbar>

          <div className={`inboxMainContainer ${isOnInboxPage}`}>
            {isOnInboxPage ? (
              <LeadsList
                inboxMessages={regularLeads}
                userId={userId}
                selectedLeadId={selectedLeadId}
                isLeadsEmpty={isLeadsEmpty}
                onClick={handleLeadChange}
                messagesLoading={messagesLoading}
                loadMoreMessages={loadMoreMessages}
                clientsAssigned={userData?.tenantId}
              />
            ) : null}
            <InboxContextProvider>
              <Wrapper>
                <InboxChatNdHeader
                  isOnInboxPage={isOnInboxPage}
                  openSopDrawer={openSopDrawer}
                  isMaximizedPnAOpen={isMaximizedPnAOpen}
                >
                  {archiveClick && chatArchivedMsg && (
                    <ArchiveConfirmation
                      chatArchivedMsg={chatArchivedMsg}
                      archiveClick={archiveClick}
                    />
                  )}
                  <>
                    <LeadHeader
                      selectedLeadId={selectedLeadId}
                      leadData={leadData || {}}
                      leadUpdateMutate={leadUpdateMutate}
                      apartmentData={apartmentData}
                      getScheduledLeadMessages={getScheduledLeadMessages}
                      refetchLeadInfo={refetchLeadInfo}
                    />
                    <div className="chatContainer">
                      <ChatContainer
                        {...{
                          closedReason: leadData?.closedReason || '',
                          botTurningOffReason:
                            leadData?.botTurningOffReason || '',
                          chatArchivedMsg,
                          archiveClick,
                          setMessageInput,
                          timeZone: apartmentData?.timeZone,
                          selectedLeadId,
                          showNewMsgNotification,
                          hideShowNewMessageNotification,
                          leadData
                        }}
                      />
                      <ChatInputBar
                        {...{
                          selectedLeadId,
                          messageInput,
                          setMessageInput,
                          isArchived: leadData?.isArchived || false,
                          preference: leadData?.preference || {},
                          emailOptIn: leadData?.emailOptIn,
                          textOptIn: leadData?.textOptIn,
                          leadTenantId: leadData?.tenant?._id,
                          onClick: handleLeadChange,
                          apartmentId,
                          leadUpdateMutate,
                          setArchiveClick,
                          archiveClick,
                          userId,
                          updatedLeadId,
                          messageSent,
                          handleOutgoingMessage,
                          newNoteInsertionInProgress,
                          handleNoteCreation,
                          handleCallLogCreation,
                          handleOutgoingScheduledMessage,
                          apartmentTimezone: apartmentData?.timeZone,
                          isBotActive:
                            !!leadEnabled &&
                            !!communityEnabled &&
                            !!clientEnabled
                        }}
                      />
                    </div>
                  </>
                  <NotificationPopup
                    selectedLeadId={selectedLeadId}
                    apartmentId={apartmentId}
                  />
                </InboxChatNdHeader>
                <LeadContainerInfo {...{ ...leadContainerProps }} />
              </Wrapper>
            </InboxContextProvider>
          </div>
          <SOPAndAgentNotes
            open={openSopDrawer}
            onClose={SOPDrawerToggleHandler}
            leadData={leadData}
          />
          {leadData?._id && (
            <MaximisedPnAWidget
              onClose={handlePNAExpansionToggle}
              isOpen={isMaximizedPnAOpen}
              leadUpdateMutate={leadUpdateMutate}
              selectedLeadId={leadData?._id}
              handleAutofillData={handleAutofillData}
              key={leadData?._id}
            />
          )}
        </>
      )}
    </Layout>
  );
};

export default InboxV2;
